import create, { GetState, SetState } from "zustand";

import { MAX_SURVEY_ANSWER_LENGTH } from "@performyard/models";
import { SurveyAnswerJson, SurveyQuestionJson } from "@performyard/service-contracts";

import { scrollToInvalidQuestion } from "../utils";

import { AUTHOR_SURVEY_MODALS, AuthorSurveyStore } from "./model";

const authorSurveyStore = (set: SetState<AuthorSurveyStore>, get: GetState<AuthorSurveyStore>) => ({
	answers: {},
	errors: {},
	questions: [],
	modal: null,
	setters: {
		setAnswer: (questionId: string, answer: SurveyAnswerJson["value"]) => {
			// answer is always either null, a string, or an array of strings
			// a null answer, empty string, or empty array should remove the answer from the store
			if (!answer || answer?.length === 0) {
				const { [questionId]: _, ...rest } = get().answers;
				set({ answers: rest });
			} else {
				set({ answers: { ...get().answers, [questionId]: answer } });
			}
		},
		setQuestions: (questions: SurveyQuestionJson[]) => set({ questions }),
		setErrors: (errors: Record<string, string>) => set({ errors }),
		setModal: (modal: AUTHOR_SURVEY_MODALS | null) => set({ modal }),
	},
	actions: {
		validateAnswers: (
			onValid?: (answers: Record<string, SurveyAnswerJson["value"]>) => void,
			onInvalid?: () => void
		) => {
			set({ errors: {} });
			const answers = get().answers;
			const questions = get().questions;
			const errors = get().errors;

			const parsedAnswers = { ...answers };

			for (const question of questions) {
				const answer = answers[question._id];
				if (question.question_settings?.is_required && !answer) {
					errors[question._id] = "Question is required";
				}
				if (!answer) {
					continue;
				}

				switch (question.type) {
					case "text":
						if (answer && typeof answer !== "string") {
							errors[question._id] = "Answer must be a string";
						} else if (answer && typeof answer === "string" && answer.length > MAX_SURVEY_ANSWER_LENGTH) {
							errors[question._id] =
								`Max character count of ${MAX_SURVEY_ANSWER_LENGTH} has been reached`;
						}
						parsedAnswers[question._id] = answer;
						break;
					default:
						parsedAnswers[question._id] = answer;
						break;
				}
			}
			if (Object.keys(errors).length > 0) {
				set({ errors });
				const firstInvalidQuestion = questions.find((question) => errors[question._id]);
				scrollToInvalidQuestion(firstInvalidQuestion?._id ?? Object.keys(errors)[0]);
				onInvalid?.();
			} else {
				onValid?.(parsedAnswers);
			}
		},
		clearAnswers: () => set({ answers: {}, errors: {} }),
	},
});

export const useAuthorSurveyStore = create<AuthorSurveyStore>(authorSurveyStore);

export const useAnswers = () => useAuthorSurveyStore((state) => state.answers);
export const useModal = () => useAuthorSurveyStore((state) => state.modal);
export const useSetters = () => useAuthorSurveyStore((state) => state.setters);
export const useErrors = () => useAuthorSurveyStore((state) => state.errors);
export const useActions = () => useAuthorSurveyStore((state) => state.actions);
